import { Button, DialogActions, DialogContent, Divider, FormControl, Grid, TextField } from "@mui/material";
import DialogHESP from "../DialogHESP/DialogHESP";
import DialogFooterWithLoading from "../Loading/DialogFooterWithLoading";
import React, { useEffect, useState } from "react";
import gql from "graphql-tag";
import { useMutation } from "react-apollo";

import { toast } from "react-toastify";
import { TOAST_SETTINGS } from "../BillingDetails/AddPaymentMethodDialog";
import { GET_DATACENTER } from "../../views/App/TLADatacenter";
import { GetDatacenter_datacenter_tla_installContexts } from "../../views/App/__generated__/GetDatacenter";

import HESPFormLabel from "../HESPFormLabel/HESPFormLabel";
import { v4 } from "uuid";
import { Add } from "@mui/icons-material";
import TLALabelsManager from "./TLALabelsManager";
import SwitchWithTitle from "../SwitchWithTitle/SwitchWithTitle";
import {
    UpdateInstallContextMutation,
    UpdateInstallContextMutationVariables,
} from "./__generated__/UpdateInstallContextMutation";

interface Props {
    open: boolean;
    datacenterId: string;
    installContext: GetDatacenter_datacenter_tla_installContexts;
    onClose: () => void;
}

export const UPDATE_TLA_INSTALL_CONTEXT = gql`
    mutation UpdateInstallContextMutation($input: UpdateInstallContextInput!) {
        updateInstallContext(input: $input) {
            id
        }
    }
`;

export default function TLAUpdateInstallContextDialog({ onClose, open, installContext, datacenterId }: Props) {
    const [updateInstallContext, { loading }] = useMutation<
        UpdateInstallContextMutation,
        UpdateInstallContextMutationVariables
    >(UPDATE_TLA_INSTALL_CONTEXT);

    const [name, setName] = useState<string>(installContext.name ?? "");
    const [description, setDescription] = useState<string>(installContext.description ?? "");
    const [labels, setLabels] = useState<Record<string, string>>(arrayToRecord(installContext.labels));
    const [nodeLabels, setNodeLabels] = useState<Record<string, string>>(arrayToRecord(installContext.nodeLabels));
    const [privateCIDR, setPrivateCIDR] = useState<string>(installContext.privateId ?? "");
    const [privateId, setPrivateId] = useState<string>(installContext.privateCIDR ?? "");
    const [disableVaultSSH, setDisableVaultSSH] = useState<boolean>(installContext.disableVaultSSH);
    const [upgradeDocker, setUpgradeDocker] = useState<boolean>(installContext.upgradeDocker);

    const [labelsOk, setLabelsOk] = useState<boolean>(true);
    const [nodeLabelsOk, setNodeLabelsOk] = useState<boolean>(true);

    useEffect(() => {
        setName(installContext.name ?? "");
        setDescription(installContext.description ?? "");
        setLabels(arrayToRecord(installContext.labels));
        setNodeLabels(arrayToRecord(installContext.nodeLabels));
        setPrivateCIDR(installContext.privateCIDR ?? "");
        setPrivateId(installContext.privateId ?? "");
        setDisableVaultSSH(installContext.disableVaultSSH);
        setUpgradeDocker(installContext.upgradeDocker);
        setNodeLabelsOk(true);
        setLabelsOk(true);
    }, [open, installContext]);

    function onChangeLabel(idx: string, value: string) {
        const copyLabels = labels;
        copyLabels[idx] = value;
        setLabels(copyLabels);
        setLabelsOk(validLabels(Object.values(copyLabels)));
    }

    const regex = /([A-z0-9/+-.,])*=([A-z0-9/+-.,])*/;

    function arrayToRecord(lbls: string[]): Record<string, string> {
        const record: Record<string, string> = {};

        lbls.forEach((label) => (record[v4()] = label));
        return record;
    }

    function onChangeNodeLabel(idx: string, value: string) {
        const copyLabels = nodeLabels;
        copyLabels[idx] = value;
        setNodeLabels(copyLabels);
        setNodeLabelsOk(validLabels(Object.values(copyLabels)));
    }

    function validLabels(lbls: string[]) {
        let isValid = true;
        lbls.forEach((label) => {
            if (!regex.test(label)) {
                isValid = false;
            }
        });
        return isValid;
    }

    function onRemoveLabel(idx: string) {
        const newList: Record<string, string> = {};
        for (let key in labels) {
            if (key !== idx) {
                newList[key] = labels[key];
            }
        }
        setLabels(newList);
        setLabelsOk(validLabels(Object.values(newList)));
    }

    function onRemoveNodeLabel(idx: string) {
        const newList: Record<string, string> = {};
        for (let key in nodeLabels) {
            if (key !== idx) {
                newList[key] = nodeLabels[key];
            }
        }
        setNodeLabels(newList);
        setNodeLabelsOk(validLabels(Object.values(newList)));
    }

    async function onConfirm() {
        if (labelsOk && nodeLabelsOk) {
            try {
                await updateInstallContext({
                    variables: {
                        input: {
                            name: name.trim().length > 0 ? name : undefined,
                            description: description.trim().length > 0 ? description : undefined,
                            id: installContext.id,
                            labels: Object.values(labels),
                            nodeLabels: Object.values(nodeLabels),
                            disableVaultSSH,
                            privateCIDR: privateCIDR.trim().length > 0 ? privateCIDR : undefined,
                            privateId: privateId.trim().length > 0 ? privateId : undefined,
                            upgradeDocker,
                        },
                    },
                    refetchQueries: [
                        {
                            query: GET_DATACENTER,
                            variables: {
                                id: datacenterId,
                            },
                        },
                    ],
                });
                onClose();
            } catch (e) {
                toast.error("Error while removing device context", TOAST_SETTINGS);
            }
        }
    }

    return (
        <DialogHESP
            title={"Update device context"}
            open={open}
            onClose={onClose}
            content={
                <>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Name" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={name}
                                        placeholder="Name"
                                        onChange={(e: any) => setName(e.target.value)}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Description" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={description}
                                        placeholder="Description"
                                        onChange={(e: any) => setDescription(e.target.value)}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Private CIDR" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={privateCIDR}
                                        placeholder="Private CIDR"
                                        onChange={(e: any) => setPrivateCIDR(e.target.value)}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Private ID" />
                                    <TextField
                                        variant="outlined"
                                        fullWidth
                                        defaultValue={privateId}
                                        placeholder="Private ID"
                                        onChange={(e: any) => setPrivateId(e.target.value)}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Disable Vault SSH" />
                                    <SwitchWithTitle
                                        checked={disableVaultSSH}
                                        onChange={() => setDisableVaultSSH(!disableVaultSSH)}
                                        title="Disable Vault SSH"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth>
                                    <HESPFormLabel label="Upgrade Docker" />
                                    <SwitchWithTitle
                                        checked={upgradeDocker}
                                        onChange={() => setUpgradeDocker(!upgradeDocker)}
                                        title="Upgrade Docker"
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                Labels
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <TLALabelsManager
                                        labels={labels}
                                        onChangeLabel={(idx: string, val: string) => onChangeLabel(idx, val)}
                                        onRemoveLabel={(idx: string) => onRemoveLabel(idx)}
                                        regex={regex}
                                    />
                                </FormControl>
                                <FormControl fullWidth style={{ display: "flex", justifyContent: "center" }}>
                                    <Button
                                        color="secondary"
                                        startIcon={<Add />}
                                        onClick={() => {
                                            const copyLabels = labels;
                                            copyLabels[v4()] = "";
                                            setLabels({ ...copyLabels });
                                            setLabelsOk(validLabels(Object.values(copyLabels)));
                                        }}
                                    >
                                        Add label
                                    </Button>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider />
                            </Grid>
                            <Grid item xs={12}>
                                Node labels
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <TLALabelsManager
                                        labels={nodeLabels}
                                        onChangeLabel={(idx: string, val: string) => onChangeNodeLabel(idx, val)}
                                        onRemoveLabel={(idx: string) => onRemoveNodeLabel(idx)}
                                        regex={regex}
                                    />
                                </FormControl>
                                <FormControl fullWidth style={{ display: "flex", justifyContent: "center" }}>
                                    <Button
                                        color="secondary"
                                        startIcon={<Add />}
                                        onClick={() => {
                                            const copyLabels = nodeLabels;
                                            copyLabels[v4()] = "";
                                            setNodeLabels({ ...copyLabels });
                                            setNodeLabelsOk(validLabels(Object.values(copyLabels)));
                                        }}
                                    >
                                        Add node label
                                    </Button>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <DialogFooterWithLoading
                            isLoading={loading}
                            onCancelClick={onClose}
                            onActionClick={onConfirm}
                            defaultText={"Update"}
                            loadingText={"Updating..."}
                        />
                    </DialogActions>
                </>
            }
        />
    );
}
